/**
 * Field definitions shared by the staff "request a correction" dialog and the
 * student-facing correction form. `columns` are the actual submission columns
 * stored on the correction request (documents use the pseudo keys
 * `id_photo` / `passport_photo`).
 */
export type FixFieldKind = "text" | "email" | "textarea" | "date" | "select" | "tags" | "phone" | "file";

export interface FixFieldDef {
  key: string;
  label: string;
  hint?: string;
  group: string;
  kind: FixFieldKind;
  columns: string[];
  options?: string[];
  accept?: string;
}

export const DIETARY_OPTIONS = ["Vegetarian", "Vegan", "Halal", "Kosher", "Gluten-free", "Dairy-free", "Pescatarian", "None"];
export const COURSE_OPTIONS = ["General English", "Business English", "Academic English"];
export const SEVERITY_OPTIONS = ["mild", "moderate", "severe"];

export const FIX_FIELDS: FixFieldDef[] = [
  // Documents
  { key: "passport_photo", label: "Passport snapshot", hint: "Photo/holder details page", group: "Documents", kind: "file", columns: ["passport_photo"], accept: "image/*,application/pdf" },
  { key: "id_photo", label: "ID photo for student card", hint: "Head & shoulders photo", group: "Documents", kind: "file", columns: ["id_photo"], accept: "image/*" },

  // Personal
  { key: "full_name", label: "Full name (as on passport)", group: "Personal details", kind: "text", columns: ["full_name"] },
  { key: "preferred_name", label: "Preferred name", group: "Personal details", kind: "text", columns: ["preferred_name"] },
  { key: "date_of_birth", label: "Date of birth", group: "Personal details", kind: "date", columns: ["date_of_birth"] },
  { key: "course", label: "Course", group: "Personal details", kind: "select", columns: ["course"], options: COURSE_OPTIONS },
  { key: "nationality", label: "Nationality", group: "Personal details", kind: "text", columns: ["nationality"] },

  // Contact
  { key: "whatsapp", label: "WhatsApp number", group: "Contact", kind: "phone", columns: ["whatsapp_country_code", "whatsapp_number", "whatsapp_e164"] },
  { key: "email", label: "Email address", group: "Contact", kind: "email", columns: ["email"] },

  // Address
  { key: "home_address_line1", label: "Address line 1", group: "Home address", kind: "text", columns: ["home_address_line1"] },
  { key: "home_address_line2", label: "Address line 2", group: "Home address", kind: "text", columns: ["home_address_line2"] },
  { key: "home_city", label: "City", group: "Home address", kind: "text", columns: ["home_city"] },
  { key: "home_postal_code", label: "Postal code", group: "Home address", kind: "text", columns: ["home_postal_code"] },
  { key: "home_country", label: "Country", group: "Home address", kind: "text", columns: ["home_country"] },

  // Passport details
  { key: "passport_number", label: "Passport number", group: "Passport details", kind: "text", columns: ["passport_number"] },
  { key: "passport_expiry", label: "Passport expiry", group: "Passport details", kind: "date", columns: ["passport_expiry"] },

  // Dietary & medical
  { key: "dietary_tags", label: "Dietary requirements", group: "Dietary & medical", kind: "tags", columns: ["dietary_tags"], options: DIETARY_OPTIONS },
  { key: "dietary_notes", label: "Dietary notes", group: "Dietary & medical", kind: "textarea", columns: ["dietary_notes"] },
  { key: "allergies", label: "Allergies", group: "Dietary & medical", kind: "textarea", columns: ["allergies"] },
  { key: "allergy_severity", label: "Allergy severity", group: "Dietary & medical", kind: "select", columns: ["allergy_severity"], options: SEVERITY_OPTIONS },
  { key: "medical_notes", label: "Medical notes", group: "Dietary & medical", kind: "textarea", columns: ["medical_notes"] },

  // Next of kin
  { key: "nok_name", label: "Next of kin — name", group: "Next of kin", kind: "text", columns: ["nok_name"] },
  { key: "nok_relationship", label: "Next of kin — relationship", group: "Next of kin", kind: "text", columns: ["nok_relationship"] },
  { key: "nok_whatsapp", label: "Next of kin — WhatsApp", group: "Next of kin", kind: "phone", columns: ["nok_whatsapp_country_code", "nok_whatsapp_number", "nok_whatsapp_e164"] },
  { key: "nok_email", label: "Next of kin — email", group: "Next of kin", kind: "email", columns: ["nok_email"] },
];

export const FIX_GROUPS = Array.from(new Set(FIX_FIELDS.map((f) => f.group)));

/** Expand selected field keys into the columns stored on the correction request. */
export function keysToColumns(keys: string[]): string[] {
  const out: string[] = [];
  for (const k of keys) {
    const def = FIX_FIELDS.find((f) => f.key === k);
    if (def) out.push(...def.columns);
  }
  return Array.from(new Set(out));
}

/** Resolve stored columns back into the field definitions to render. */
export function columnsToFields(columns: string[]): FixFieldDef[] {
  const set = new Set(columns || []);
  return FIX_FIELDS.filter((f) => f.columns.some((c) => set.has(c)));
}

export function fieldLabels(columns: string[]): string[] {
  return columnsToFields(columns).map((f) => f.label);
}
